import { Box, Divider, Grid, Typography, makeStyles } from '@material-ui/core';
import { FiberManualRecord, Info } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Button, SubmitButton, PDFPreview, StatusChip } from '@roc/ui';
import { Add, Check, Send, Clear, Refresh } from '@material-ui/icons';
import { AddEditInsuranceQuote } from './addEditInsuranceQuote';
import React, { useEffect, useState } from 'react';
import { InsuranceQuotesTable } from './insuranceQuotesTable';
import {
  InsuranceInformationBaseStore,
} from '@roc/feature-insurance-review';
import { Layout } from '@roc/feature-app-core';
import { ElmsureForm } from './requestElmsureForm';
import { ResendInsuranceQuoteForm, WithDocumentActions } from '@roc/feature-documents';
import { InsuranceInformationFinalDocumentSelection } from './insuranceInformationFinalDocumentSelection';
import { InsuranceInformationFinalDocumentsSummary } from './insuranceInformationFinalDocumentsSummary';
import Chip from '@material-ui/core/Chip';
import { darken } from '@material-ui/core/styles';
import { InsuranceGuidelinesModal } from './guidelines/insuranceGuidelinesModal';
import { UnclassifiedDocumentsTable } from './unclassifiedDocumentsTable';
import { isFundedLoan } from '@roc/client-portal-shared/utils';
import { Document } from '@roc/feature-types';
import { formatCurrency } from '@roc/ui/utils';

export type InsuranceInformationProps = {
  store: InsuranceInformationBaseStore;
  loanId: string;
  taskId: string;
  document?: Document;
  showTitle?: boolean;
  handleClose: () => void;
};

const styles = makeStyles(() => ({
  guidelinesContainer: {
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  guidelinesText: {
    fontSize: '14px',
    paddingLeft: '5px',
    textDecoration: 'underline',
    textTransform: 'uppercase',
  },
  icon: {
    fontSize: '24px',
  },
  buttonSeparator: {
    marginRight: '8px',
  }
}));

export const InsuranceInformation = observer(
  (props: InsuranceInformationProps) => {
    const { store, loanId, taskId, handleClose, showTitle = true } = props;
    const {
      isSaveInsuranceQuoteActive,
      quotes,
      finalChoiceQuotes,
      propertiesMissingQuotes,
      guidelinesData,
      loanDetails,
      quoteOptions,
      globalStore
    } = store;
    const [
      isFinalDocumentSelectionOpen,
      setFinalDocumentSelectionOpen,
    ] = useState(false);
    const [guidelinesModalOpen, setGuidelinesModalOpen] = useState(false);
    const { userFeatures } = globalStore
    const { approveInsuranceButton } = userFeatures
    const loanStatus = loanDetails?.status;
    const classes = styles();
    const maximumInsuranceAllowed = loanDetails?.maximumInsuranceAllowed || 0;

    useEffect(() => {
      store.init(loanId, taskId);
    }, [store, loanId, taskId]);

    useEffect(() => {
      store.loadFinalDocuments();
      store.loadUnclassifiedDocuments();
    }, []);

    const handleApproveInsurance = async () => {
      const approvalType = await store.approveInsurance();
      if (approvalType.includes('THIRD_PARTY_APPROVED')) {
        setFinalDocumentSelectionOpen(true);
      } else {
        handleClose();
      }
    };

    const handleRefreshQuotes = async () => {
      try {
        await store.refreshQuotes();
        await store.init(loanId, taskId);
        await store.loadFinalDocuments();
      } catch (error) {
        console.error("Error during quote refresh:", error);
      }
    };

    return (
      <>
        {isSaveInsuranceQuoteActive ? (
          <AddEditInsuranceQuote store={store} showCancelOption={true} />
        ) : isFinalDocumentSelectionOpen ? (
          <InsuranceInformationFinalDocumentSelection
            store={store}
            onCancel={() => setFinalDocumentSelectionOpen(false)}
            onApprove={handleClose}
          />
        ) : (
          <div style={{ backgroundColor: '#FFF', width: '100%' }}>
            <Layout>
              <Grid container spacing={2}>
                {showTitle &&
                  <Grid item xs={12} style={{ margin: '0 0 15px 0' }}>
                    <Grid container spacing={1} item xs={12}>
                      {showTitle &&
                        <Grid item>
                          <Typography variant="h4">
                            Insurance Information
                          </Typography>
                        </Grid>
                      }
                    </Grid>
                    <Divider />
                  </Grid>
                }
                {store?.totalInsurancePremium <= maximumInsuranceAllowed && (
                  <Grid item xs={12}>
                    <Alert
                      severity="info"
                    >
                      <AlertTitle>
                        <b>The maximum allowed insurance premium is:</b>
                      </AlertTitle>
                      <Typography variant="h4" align="left">
                        <b>{formatCurrency(maximumInsuranceAllowed)}</b>
                      </Typography>
                      <p>
                        Your insurance premium is currently lower than this.
                      </p>
                    </Alert>
                  </Grid>
                )}
                {store?.totalInsurancePremium > maximumInsuranceAllowed && (
                  <Grid item xs={12}>
                    <Alert
                      severity="warning"
                    >
                      <AlertTitle>
                        <b>The maximum allowed insurance premium is:</b>
                      </AlertTitle>
                      <Typography variant="h4" align="left">
                        <b>{formatCurrency(maximumInsuranceAllowed)}</b>
                      </Typography>
                      <p>
                        Your current insurance premium exceeds the maximum allowed. If you cannot find a quote with a lower insurance premium than this, a task will be created for the underwriter to check the loan amount. Please reach out to your underwriter for assistance.
                      </p>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Alert
                    severity="info"
                    onClick={() => setGuidelinesModalOpen(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    <AlertTitle>
                      <b>THIRD-PARTY INSTRUCTIONS</b>
                    </AlertTitle>
                    <p>
                      Click{' '}
                      <span style={{ textDecoration: 'underline' }}>here</span>{' '}
                      for insurance requirements
                    </p>
                  </Alert>
                </Grid>
                {propertiesMissingQuotes.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      <AlertTitle>
                        <b>
                          Insurance Information is missing for below properties
                        </b>
                      </AlertTitle>
                      <ul>
                        {propertiesMissingQuotes.map(propertyMissingQuote => (
                          <li>{propertyMissingQuote}</li>
                        ))}
                      </ul>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12} container className={classes.header}>
                  <Grid item>
                    <Typography variant="h6">
                      To add details about the <b>insurance</b> (or){' '}
                      <b>quote</b> you received elsewhere, click the button
                      below
                    </Typography>
                  </Grid>
                </Grid>
                {!isFundedLoan(loanStatus) && (//NOT FUNDED
                  <Grid
                    container
                    item
                    xs={12}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid item>
                      <Grid container spacing={1}>
                        <Grid item>
                          <WithDocumentActions
                            document={props.document}
                            component={(btnProps) => (
                              <Button
                                {...btnProps}
                                testId="addQuote"
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                onClick={() => {
                                  store.handleOpenSave();
                                }}
                              >
                                Add Insurance / Quote
                              </Button>
                            )}
                          />
                        </Grid>
                        <Grid item>
                          {store.quotes.some(
                            quote =>
                              quote.quoteShowId.includes('Elmsure') &&
                              quote.insurancePremium != null
                          ) && (
                              <WithDocumentActions
                                document={props.document}
                                component={(btnProps) => (
                                  <SubmitButton
                                    {...btnProps}
                                    className={classes.buttonSeparator}
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Send />}
                                    testId="resendElmsureQuote"
                                    onClick={() => store.resendElmsureQuote()}
                                    submitDialogProps={{
                                      submitValidation: () => {
                                        store.resendElmsureQuoteStore.runFormValidation();
                                        return store.resendElmsureQuoteStore.form.meta.isValid;
                                      },
                                      title: `Confirm borrower email address to resend the quote`,
                                      body: (
                                        <ResendInsuranceQuoteForm
                                          store={store.resendElmsureQuoteStore}
                                        />
                                      ),
                                      okButtonText: 'Submit',
                                      cancelButtonText: 'Close',
                                    }}
                                  >
                                    RESEND ELMSURE QUOTE
                                  </SubmitButton>
                                )}
                              />
                            )}
                          <WithDocumentActions
                            document={props.document}
                            component={(btnProps) => (
                              <SubmitButton
                                {...btnProps}
                                className={classes.buttonSeparator}
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                testId="requestElmsureQuote"
                                onClick={() => store.requestQuoteFromElmsure()}
                                submitDialogProps={{
                                  submitValidation: () =>
                                    store.validateElmsureQuoteSubmission(),
                                  title: `Request Elmsure Quote`,
                                  body: <ElmsureForm store={store} />,
                                  okButtonText: 'Submit',
                                  cancelButtonText: 'Close',
                                }}
                              >
                                REQUEST ELMSURE QUOTE
                              </SubmitButton>
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Button
                        testId="refreshQuotes"
                        variant="contained"
                        color="primary"
                        startIcon={<Refresh />}
                        onClick={() => {
                          handleRefreshQuotes();
                        }}
                      >
                        REFRESH QUOTES
                      </Button>
                    </Grid>
                  </Grid>
                )}
                {store.insuranceQuoteFormStore.isInternal && <>
                  {(Array.isArray(store.unclassifiedDocuments) && (store.unclassifiedDocuments.length > 0)) &&
                    <Grid item xs={12}>
                      <Alert severity="warning" onClick={() => setGuidelinesModalOpen(true)} style={{ cursor: 'pointer' }}>
                        <AlertTitle>
                          <b>
                            Unclassified Documents Pending
                          </b>
                        </AlertTitle>
                        <p>You have unclassified documents waiting for review.To classify them, a quote is required.If a quote has not been created yet, please create one first.</p>
                      </Alert>
                    </Grid>
                  }
                  <Grid item xs={12}>
                    <UnclassifiedDocumentsTable
                      title={'Unclassified Documents'}
                      documents={store.unclassifiedDocuments}
                      emptyMessage='No documents have been added yet'
                      store={store}
                      classifyOptions={quoteOptions}
                      allowActions={store.insuranceQuoteFormStore.isInternal}
                    />
                  </Grid>
                </>
                }
                <Grid item xs={12}>
                  <InsuranceQuotesTable
                    title={'Insurance / Quotes'}
                    quotes={quotes}
                    emptyMessage={
                      'No insurance information have been added yet'
                    }
                    store={store}
                    allowActions={true}
                  />
                </Grid>
                <Grid item xs={12} />
                <Grid item xs={12}>
                  <InsuranceQuotesTable
                    title={'Insurance / Quotes (Selected as Final Choice)'}
                    quotes={finalChoiceQuotes}
                    emptyMessage={
                      'No insurance / quote have been selected as final choice yet'
                    }
                    store={store}
                    allowActions={true}
                  />
                </Grid>
                {store.isInsuranceApproved && (
                  <Grid item xs={12}>
                    <InsuranceInformationFinalDocumentsSummary store={store} />
                  </Grid>
                )}
                {store.insuranceApprovalErrors?.length > 0 && (
                  <Grid item xs={12}>
                    <InsuranceApprovalErrors
                      errors={store.insuranceApprovalErrors}
                    />
                  </Grid>
                )}
                <Grid
                  container
                  spacing={1}
                  item
                  xs={12}
                  direction="row-reverse"
                >
                  {(store.insuranceQuoteFormStore.isInternal &&
                    !store.isInsuranceApproved &&
                    (!isFundedLoan(loanStatus)) &&
                    approveInsuranceButton) && (
                      <Grid item>
                        <WithDocumentActions
                          document={props.document}
                          component={(btnProps) => (
                            <Button
                              {...btnProps}
                              testId="approveInsurance"
                              variant="contained"
                              color="primary"
                              startIcon={<Check />}
                              onClick={() => handleApproveInsurance()}
                            >
                              Approve Insurance
                            </Button>
                          )}
                        />
                      </Grid>
                    )}
                  {store.insuranceQuoteFormStore.isInternal &&
                    store.isInsuranceApproved &&
                    (!isFundedLoan(loanStatus)) && (
                      <Grid item>
                        <WithDocumentActions
                          document={props.document}
                          component={(btnProps) => (
                            <Button
                              {...btnProps}
                              testId="revertInsurance"
                              variant="outlined"
                              color="primary"
                              onClick={async () => {
                                await store.revertInsurance();
                                handleClose();
                              }}
                              startIcon={<Clear />}
                            >
                              Revert Insurance
                            </Button>
                          )}
                        />
                      </Grid>
                    )}
                </Grid>
              </Grid>
            </Layout>
          </div>
        )}
        <PDFPreview
          open={store.isPdfPreviewModalOpen}
          title={store.pdfTitle}
          data={store.pdfData}
          headers={{}}
          fileName={store.pdfTitle}
          onClose={() => {
            store.pdfTitle = null;
            store.pdfData = null;
            store.isPdfPreviewModalOpen = false;
          }}
        />
        <InsuranceGuidelinesModal
          open={guidelinesModalOpen}
          onClose={() => setGuidelinesModalOpen(false)}
          data={guidelinesData}
        />
      </>
    );
  });


const InsuranceApprovalErrors = ({ errors }) => {
  return (
    <Alert severity={'error'} style={{ width: '100%', padding: '15px' }}>
      <AlertTitle>{'Insurance approval errors'}</AlertTitle>
      <Grid container spacing={1} direction='column' style={{ padding: '5px' }}>
        {errors?.map((error) =>
          <Grid container spacing={1} item direction='row'>
            <Box display={'flex'} alignItems={'center'}>
              <FiberManualRecord style={{ fontSize: '10px', margin: '0 5px' }} />
              <Typography> {error} </Typography>
            </Box>
          </Grid>)}
      </Grid>
    </Alert>
  )
};
